import React from 'react';
import './CustomCheckbox.scss';

/**
 * Checkbox used by every row of the file tree.
 *
 * This deliberately does NOT use `@mui/material/Checkbox`: that renders
 * ButtonBase + SwitchBase + ripple machinery and reads the theme, per instance.
 * With a fully expanded tree that was measurably the single most expensive part
 * of a row. The visuals were already fully custom (plain spans), so all MUI
 * bought us was the input plumbing — which a native input does for free.
 *
 * The markup keeps a real `<input type="checkbox">` so `onChange` still receives
 * a genuine change event with `event.target.checked`, which is what the tree
 * view's selection plugin reads.
 */
type CustomCheckboxProps = {
  checked?: boolean;
  indeterminate?: boolean;
  disabled?: boolean;
  /** Provided by the tree view; the tree decides whether to render us at all. */
  visible?: boolean;
  className?: string;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  tabIndex?: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
};

export const CustomCheckbox = React.memo(
  React.forwardRef<HTMLInputElement, CustomCheckboxProps>(
    function CustomCheckbox(
      {
        checked,
        indeterminate,
        disabled,
        visible,
        className,
        onChange,
        tabIndex,
        ...props
      },
      ref
    ) {
      const inputRef = React.useRef<HTMLInputElement | null>(null);

      const handleRef = React.useCallback(
        (node: HTMLInputElement | null) => {
          inputRef.current = node;
          if (typeof ref === 'function') ref(node);
          else if (ref) {
            (ref as React.MutableRefObject<HTMLInputElement | null>).current =
              node;
          }
        },
        [ref]
      );

      // `indeterminate` only exists as a DOM property, never as an attribute.
      React.useEffect(() => {
        if (inputRef.current) {
          inputRef.current.indeterminate = !!indeterminate;
        }
      }, [indeterminate]);

      const state = indeterminate
        ? 'indeterminate'
        : checked
        ? 'checked'
        : 'unchecked';

      return (
        <span
          {...props}
          className={className ? `cd-checkbox ${className}` : 'cd-checkbox'}
          data-state={state}
          data-disabled={disabled ? 'true' : undefined}
        >
          <input
            ref={handleRef}
            type="checkbox"
            className="cd-checkbox__input"
            checked={!!checked}
            disabled={!!disabled}
            tabIndex={tabIndex}
            onChange={onChange}
          />
          <span className="cd-checkbox__box" aria-hidden="true" />
        </span>
      );
    }
  )
);
